<template>
  <el-dialog
    :visible.sync="visibleProxy"
    width="920px"
    :close-on-click-modal="false"
    custom-class="invoice-modal"
  >
    <!-- Header -->
    <template #title>
      <div class="modal-title">
        <div class="title-row">
          <div class="title-text">
            <div class="invoice-name">{{ header.invoice_name || '—' }}</div>
            <div class="invoice-sub">{{ header.label || '—' }}</div>
          </div>

          <el-tag :type="statusType" effect="dark" class="status-pill">
            {{ (header.status || '—').toUpperCase() }}
          </el-tag>
        </div>

        <div class="meta-row">
          <div class="meta-chip">
            <span class="k">Net total</span>
            <span class="v">{{ money(header.net_total, header.currency) }}</span>
          </div>
          <div class="meta-chip">
            <span class="k">Invoice ID</span>
            <span class="v">#{{ header.id }}</span>
          </div>
          <div class="meta-chip" v-if="header.period_start || header.period_end">
            <span class="k">Period</span>
            <span class="v">{{ header.period_start || '—' }} → {{ header.period_end || '—' }}</span>
          </div>
        </div>
      </div>
    </template>

    <!-- Body -->
    <el-skeleton v-if="loading" :rows="6" animated />

    <div v-else>
      <h4 class="section-title">Revenue Breakdown</h4>

      <el-table :data="rows" border size="small" class="zebra" empty-text="No items">
        <el-table-column prop="service_label" label="Service" min-width="180"/>
        <el-table-column label="Rate %" width="120" align="right">
          <template #default="{ row }">{{ percent(row.rate_percent) }}</template>
        </el-table-column>
        <el-table-column label="Gross" width="160" align="right">
          <template #default="{ row }">{{ money(row.gross_amount, currency) }}</template>
        </el-table-column>
        <el-table-column label="Fee" width="160" align="right">
          <template #default="{ row }">
            <span class="neg">-{{ money(row.fee_amount, currency) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Net" width="160" align="right">
          <template #default="{ row }">{{ money(row.net_amount, currency) }}</template>
        </el-table-column>
      </el-table>

      <!-- Totals strip -->
      <div class="totals-strip">
        <div class="totals-label">Total</div>
        <div class="totals-grid">
          <div class="t right">{{ money(totals.gross, currency) }}</div>
          <div class="t right neg">-{{ money(totals.fee, currency) }}</div>
          <div class="t right bold big">{{ money(totals.net, currency) }}</div>
        </div>
      </div>

      <AdminInvoiceQueryHistory
        :invoice-id="invoiceId"
        :queries="queries"
        :visible="visibleProxy"
        @updated="fetch"
      />
    </div>

    <!-- Footer -->
    <template #footer>
      <div class="footer-actions">
        <el-button class="ghost" :disabled="!canSetPaid" @click="openSetPaid">Set as Paid</el-button>
        <el-button @click="close">Close</el-button>
      </div>
    </template>

    <!-- Set as Paid modal -->
    <el-dialog title="Set as Paid" :visible.sync="setPaidOpen" width="520px" append-to-body>
      <el-form :model="setPaid" label-width="120px">
        <el-form-item label="Method">
          <el-select v-model="setPaid.method" placeholder="Select">
            <el-option label="PayPal" value="paypal" />
            <el-option label="Wise" value="wise" />
            <el-option label="Bank Transfer" value="bank_transfer" />
            <el-option label="Manual" value="manual" />
          </el-select>
        </el-form-item>
        <el-form-item label="Transaction ID">
          <el-input v-model="setPaid.txn_id" />
        </el-form-item>
        <el-form-item label="Paid At">
          <el-date-picker v-model="setPaid.paid_at" type="datetime" value-format="yyyy-MM-dd HH:mm:ss" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="setPaidOpen=false">Cancel</el-button>
        <el-button type="primary" @click="confirmSetPaid">Save</el-button>
      </span>
    </el-dialog>
  </el-dialog>
</template>

<script>
import axios from '@/plugins/axios'
import AdminInvoiceQueryHistory from './AdminInvoiceQueryHistory.vue'

export default {
  name: 'AdminInvoiceDetailModal',
  components: { AdminInvoiceQueryHistory },
  props: {
    visible: { type: Boolean, default: false },
    invoiceId: { type: [Number, String], required: true },
  },

  data () {
    return {
      visibleProxy: this.visible,
      loading: false,
      header: {},
      rows: [],
      totals: { gross: 0, fee: 0, net: 0 },
      currency: 'USD',
      queries: [],

      // payment capabilities from API
      paypalActive: false,
      wiseActive: false,

      // button loaders
      payingPaypal: false,
      payingWise: false,

      // query
      query: null,
      resolving: false,

      // set paid modal
      setPaidOpen: false,
      setPaid: { method: '', txn_id: '', paid_at: '' },

      // inline SVGs
      paypalSvg: '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M19 4.2c-1.1-.9-2.8-1.2-5-1.2H9.7c-.5 0-.9.3-1 .8L6.1 18.8c-.1.5.3 1 .8 1H10l.5-3.2c.1-.5.5-.8 1-.8h1.6c3.3 0 5.9-1.3 6.7-5.1.3-1.6.1-2.9-.8-3.9z" fill="#fff" opacity=".6"/><path d="M17.7 6.1c-.8-.8-2.2-1.1-4.1-1.1H10c-.5 0-.9.3-1 .8L7.3 18.4c-.1.5.3 1 .8 1h1.9l.4-2.6c.1-.5.5-.8 1-.8h1.6c2.4 0 4.3-.5 5.4-1.7 1.3-1.3 1.7-3.4 1.2-5.2-.3-1-1-1.9-1.9-2.6z" fill="#fff"/></svg>',
      wiseSvg: '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M4 4l10.8 3.4L9.7 13h5.5L12 20l1.4-5H8.2l5.1-5.7L4 4z" fill="#fff"/></svg>'
    }
  },
  watch: {
    visible (v) {
      this.visibleProxy = v
      this.maybeFetch()
    },
    invoiceId () {
      this.maybeFetch()
    },
    visibleProxy (v) {
      if (!v) this.$emit('update:visible', false)
    }
  },
  mounted () {
    this.maybeFetch()
  },
  computed: {
    statusType () {
      var s = String(this.header.status || '').toLowerCase()
      if (s === 'approved' || s === 'paid') return 'success'
      if (s === 'queried') return 'warning'
      if (s === 'rejected') return 'danger'
      return 'info'
    },
    isApproved () {
      return String(this.header.status || '').toLowerCase() === 'approved'
    },
    isQueried () {
      return String(this.header.status || '').toLowerCase() === 'queried'
    },
    canPayWithPayPal () {
      return this.isApproved && this.paypalActive && !this.payingPaypal
    },
    canPayWithWise () {
      return this.isApproved && this.wiseActive && !this.payingWise
    },
    canSetPaid () {
      return this.isApproved
    },
    isPaid () {
      return this.header.status === 'paid'
    },
    paypalTooltip () {
      if (this.canPayWithPayPal) return ''
      if (this.isPaid) return 'Already Paid'
      if (!this.isApproved) return 'Only approved invoices can be paid'
      if (!this.paypalActive) return 'PayPal payout not set up for this user'
      return ''
    },
    wiseTooltip () {
      if (this.canPayWithWise) return ''
      if (this.isPaid) return 'Already Paid'
      if (!this.isApproved) return 'Only approved invoices can be paid'
      if (!this.wiseActive) return 'Wise payout not set up for this user'
      return ''
    },
  },
  methods: {
    async fetch () {
      if (!this.invoiceId) return
      this.loading = true
      try {
        const resp = await axios.admin.get('/invoices/' + this.invoiceId)
        const data = resp.data || {}

        // Support multiple shapes of payload
        var inv = (data && data.invoice) ? data.invoice
          : (data && data.header) ? data.header
            : data

        this.header = {
          id:            (inv && inv.id !== undefined && inv.id !== null) ? inv.id : this.invoiceId,
          label:         (inv && (inv.label || inv.label_name)) ? (inv.label || inv.label_name) : '',
          invoice_name:  (inv && (inv.invoice_name || inv.name)) ? (inv.invoice_name || inv.name) : '',
          status:        (inv && inv.status) ? inv.status : '',
          currency:      (data && data.currency) ? data.currency : ((inv && inv.currency) ? inv.currency : 'USD'),
          net_total:     parseFloat((inv && (inv.net_total != null ? inv.net_total : inv.net)) || 0) || 0,
          gross_total:   parseFloat((inv && inv.gross_total) || 0) || 0,
          fee_total:     parseFloat((inv && inv.fee_total) || 0) || 0,
          period_start:  (inv && inv.period_start) ? inv.period_start : null,
          period_end:    (inv && inv.period_end) ? inv.period_end : null,
        }
        this.currency = this.header.currency

        // Items
        var items = (data && data.items) ? data.items
          : (data && data.breakdown) ? data.breakdown
            : (inv && inv.items) ? inv.items
              : []

        this.rows = (items || []).map(function (it) {
          var rate = parseFloat((it.rate_percent != null ? it.rate_percent : it.rate) || 0) || 0
          var gross = parseFloat((it.gross_amount != null ? it.gross_amount : it.gross) || 0) || 0
          var fee = parseFloat((it.fee_amount != null ? it.fee_amount : it.fee) || 0) || 0
          var net = parseFloat((it.net_amount != null ? it.net_amount : it.net) || 0) || 0
          return {
            id: it.id,
            service: it.service,
            service_label: it.service || 'Service',
            rate_percent: rate,
            gross_amount: gross,
            fee_amount: fee,
            net_amount: net,
          }
        })

        // Totals
        if (this.rows.length) {
          var gross = 0, fee = 0, net = 0
          for (var i = 0; i < this.rows.length; i++) {
            gross += Number(this.rows[i].gross_amount || 0)
            fee   += Number(this.rows[i].fee_amount || 0)
            net   += Number(this.rows[i].net_amount || 0)
          }
          this.totals = { gross: gross, fee: fee, net: net }
        } else {
          this.totals = {
            gross: this.header.gross_total || 0,
            fee:   this.header.fee_total || 0,
            net:   this.header.net_total || 0,
          }
        }

        // Query (latest)
        this.queries = Array.isArray(inv && inv.queries)
          ? inv.queries
          : Array.isArray(data && data.queries)
            ? data.queries
            : [];

        // Payout capabilities
        this.paypalActive = !!(data && data.paypalActive)
        this.wiseActive   = !!(data && data.wiseActive)
        this.$nextTick(() => this.markMessagesAsSeen())
      } finally {
        this.loading = false
      }
    },

    async markMessagesAsSeen () {
      try {
        const unseen = []
        this.queries.forEach(q => {
          if (Array.isArray(q.messages)) {
            q.messages.forEach(m => {
              if (!m.seen && m.sender_id) {
                unseen.push(m.id)
              }
            })
          }
        })

        if (!unseen.length) return

        await axios.admin.post('/invoices/queries/mark-seen', { message_ids: unseen })

        this.queries = this.queries.map(q => ({
          ...q,
          messages: q.messages.map(m => ({
            ...m,
            seen: unseen.includes(m.id) ? 1 : m.seen
          }))
        }))
        this.$emit('seen-updated')
      } catch (err) {
        console.error('Failed to mark messages as seen', err)
      }
    },

    maybeFetch () {
      if (this.visible && this.invoiceId) this.fetch()
    },

    // helpers
    money (n, currency) {
      var v = Number(n || 0)
      var c = currency || 'USD'
      var sym = c === 'USD' ? '$' : (c === 'EUR' ? '€' : (c === 'GBP' ? '£' : ''))
      return sym + v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    percent (n) {
      var v = Number(n || 0)
      return v.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })
    },
    prettyReason (r) {
      if (!r) return ''
      var s = String(r).replace(/_/g, ' ')
      return s.charAt(0).toUpperCase() + s.slice(1)
    },

    close () { this.visibleProxy = false },

    // set paid
    openSetPaid () { this.setPaidOpen = true },
    async confirmSetPaid () {
      await axios.admin.post('/invoices/' + this.invoiceId + '/set-paid', {
        method: this.setPaid.method,
        transaction_id: this.setPaid.txn_id,
        paid_at: this.setPaid.paid_at
      })
      this.$message.success('Marked as paid.')
      this.setPaidOpen = false
      this.$emit('changed')
      this.close()
    },
  },
}
</script>
<style scoped>
/* ---- Dialog wrapper ---- */
.invoice-modal ::v-deep(.el-dialog__header) { padding-bottom: 8px; }
.invoice-modal ::v-deep(.el-dialog__body) { padding-top: 8px; }
.invoice-modal ::v-deep(.el-dialog__footer) { padding-top: 0; }

/* ---- Header ---- */
.modal-title { display: grid; gap: 8px; }
.title-row { display:flex; align-items:center; justify-content:space-between; }
.title-text { display:flex; flex-direction:column; gap:4px; }
.invoice-name { font-size:22px; font-weight:800; letter-spacing:.2px; }
.invoice-sub { color:#6b7280; font-size:13px; }
.status-pill { text-transform: uppercase; border-radius: 999px; padding: 2px 10px; }

.meta-row { display:flex; gap:10px; flex-wrap:wrap; margin-top: 2px; }
.meta-chip {
  display:flex; gap:6px; align-items:center;
  padding:6px 10px; border-radius:10px;
  background: #f8fafc; border:1px solid #e5e7eb;
  font-size:12px;
}
.meta-chip .k { color:#6b7280; }
.meta-chip .v { font-weight:600; color:#111827; }

/* ---- Sections ---- */
.section-title { margin: 10px 0 8px; font-size:16px; font-weight:700; }

/* Table stripes */
.zebra ::v-deep(.el-table__body-wrapper tbody tr:nth-child(odd)) {
  background-color: #fafafa;
}

/* Totals strip */
.totals-strip {
  margin-top: 10px;
  padding: 14px 14px;
  background: linear-gradient(180deg, #f8fafc, #f3f4f6);
  border:1px solid #e5e7eb;
  border-radius: 12px;
  display:flex; align-items:center; justify-content:space-between;
}
.totals-label { font-weight:700; color:#374151; }
.totals-grid { display:grid; grid-template-columns: 160px 160px 180px; gap:10px; width: auto; }
.t { font-weight:600; }
.right { text-align:right; }
.bold { font-weight:800; }
.big { font-size:18px; }
.neg { color:#dc2626; }

/* Query card */
.query-card {
  margin-top: 14px;
  border-radius: 12px;
  border: 1px solid #facc15;
  background: #fffbeb;
  padding: 12px 14px;
}
.query-title { font-weight:700; color:#92400e; margin-bottom:6px; }
.qmsg { font-size:14px; color:#111827; }
.muted { color:#6b7280; }
.tiny { font-size:12px; }
.mt-4 { margin-top:4px; }
.mt-8 { margin-top:8px; }

/* Footer actions */
.footer-actions {
  display:flex;
  align-items:center;
  gap:12px;
  flex-wrap:wrap;
  justify-content: center;
}

/* Ghost button (Set as Paid) */
.ghost {
  border-radius: 12px;
  border: 1px solid #e5e7eb;
}

/* Brand buttons */
.brand-btn {
  position: relative;
  display:inline-flex; align-items:center; gap:10px;
  border: none;
  padding: 14px 22px;
  border-radius: 16px;
  font-weight:800;
  color: #fff;
  cursor: pointer;
  transition: transform .05s ease, filter .2s ease, opacity .2s ease;
  box-shadow: 0 6px 18px rgba(0,0,0,.12);
}
.brand-btn:active { transform: translateY(1px); }
.brand-btn:disabled { opacity:.45; cursor: not-allowed; filter: grayscale(.3); }
</style>